import { createClient } from '@supabase/supabase-js'

/** @typedef {import('../../types/supabase').Database} Database */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

/** @type {import('@supabase/supabase-js').SupabaseClient<Database>} */
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // Keep session alive across browser restarts + PWA reopens
    detectSessionInUrl: true,
  },
})

// ── Resilient query helper ──────────────────────────────
// Wraps any Supabase query so that if it fails with a JWT/auth error,
// we refresh the session once and retry. This catches the case where
// the JWT expires while the app is open (overnight shifts) and the
// auto-refresh timer was suspended by the OS.
//
// Usage:  const { data, error } = await resilientQuery(() =>
//           supabase.from('table').select('*').eq('id', x)
//         )
/**
 * Wraps a Supabase query with automatic JWT-refresh retry.
 * The Supabase query builder is PromiseLike (has .then) but not a full
 * Promise, so accept PromiseLike<T> here.
 * @template {{ error?: { message?: string, code?: string, status?: number } | null }} T
 * @param {() => PromiseLike<T>} queryFn
 * @returns {Promise<T>}
 */
export async function resilientQuery(queryFn) {
  const result = await queryFn()

  // Check for JWT/auth errors that a session refresh could fix
  const err = result.error
  if (err && isAuthError(err)) {
    console.warn('Query auth error, attempting session refresh:', err.message)
    const { data: refreshData } = await supabase.auth.refreshSession()
    if (refreshData?.session) {
      // Retry the query with the fresh token
      return await queryFn()
    }
    // Refresh failed — return original error
    console.warn('Session refresh failed, returning original error')
  }

  return result
}

/**
 * @param {{ message?: string, code?: string, status?: number }} err
 * @returns {boolean}
 */
function isAuthError(err) {
  const msg = (err.message || '').toLowerCase()
  const code = err.code || ''
  return (
    code === 'PGRST301' ||           // JWT expired (PostgREST)
    code === '401' ||
    msg.includes('jwt expired') ||
    msg.includes('jwt') ||
    msg.includes('invalid claim') ||
    msg.includes('token is expired') ||
    msg.includes('not authenticated') ||
    (err.status === 401)
  )
}

// ── Periodic session keep-alive ─────────────────────────
// The Supabase auto-refresh timer gets suspended when the phone
// backgrounds the app. The visibilitychange handler covers tab resume,
// but for long active sessions (overnight shifts where the phone stays
// on), the JWT can silently expire. This heartbeat refreshes every
// 10 minutes while the app is visible, preventing silent expiry.
if (typeof document !== 'undefined') {
  /** @type {ReturnType<typeof setInterval> | null} */
  let heartbeatInterval = null

  function startHeartbeat() {
    if (heartbeatInterval) return
    heartbeatInterval = setInterval(async () => {
      if (document.visibilityState !== 'visible') return
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) return
        // Check if access token expires within 5 minutes
        const expiresAt = session.expires_at // unix timestamp in seconds
        const nowSecs = Math.floor(Date.now() / 1000)
        if (expiresAt && expiresAt - nowSecs < 300) {
          await supabase.auth.refreshSession()
        }
      } catch (e) {
        // Silent — don't disrupt the user
      }
    }, 10 * 60 * 1000) // every 10 minutes
  }

  function stopHeartbeat() {
    if (heartbeatInterval) { clearInterval(heartbeatInterval); heartbeatInterval = null }
  }

  // Start heartbeat when visible, stop when hidden (save battery)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') startHeartbeat()
    else stopHeartbeat()
  })
  if (document.visibilityState === 'visible') startHeartbeat()
}

// ── Re-validate session when app returns from background ──
// On phones the browser suspends JS while backgrounded, so the
// auto-refresh timer never fires and the JWT can expire.
// Retry on failure to avoid logging out crew members due to a network blip.
if (typeof document !== 'undefined') {
  let refreshing = false
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState !== 'visible' || refreshing) return
    refreshing = true
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) { refreshing = false; return }

      // Try refreshing with retries (crew members often on spotty mobile networks)
      const delays = [0, 2000, 5000]
      for (let i = 0; i < delays.length; i++) {
        if (i > 0) await new Promise(r => setTimeout(r, delays[i]))
        const { data, error } = await supabase.auth.refreshSession()
        if (data?.session) break // success
        if (i === delays.length - 1) {
          console.warn('Session refresh failed after retries:', error?.message)
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      console.warn('Visibility refresh error:', msg)
    } finally {
      refreshing = false
    }
  })
}

/**
 * Helper: inject account_id into an object for INSERT operations.
 * Identity-preserving (generic) so downstream typed INSERT calls against the
 * Supabase Database generic see the full narrowed row type — not `Record<string, unknown>`.
 * @template {object} T
 * @param {T} obj
 * @param {string} accountId
 * @returns {T & { account_id: string }}
 */
export function withAccount(obj, accountId) {
  return { ...obj, account_id: accountId }
}
